import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useMall } from "@/context/MallContext";
import { themeOf } from "@/lib/mall";

/** Keeps ?store= on a link so the shopper never falls out of the shop they walked into. */
export function useKeepStore() {
    const { search } = useLocation();
    const store = new URLSearchParams(search).get("store");
    return (to) => {
        if (!store || to.includes("store=")) return to;
        return `${to}${to.includes("?") ? "&" : "?"}store=${store}`;
    };
}

/**
 * Where you are, in one line: Home › Shirts › Same Day. Under it, the page's own sub-menu as
 * pills (Ready-in, materials…) so the next step is always one click away. The spider web.
 */
export function Crumbs({ trail = [], pills = [], testid = "crumbs", right }) {
    const { store } = useMall();
    const theme = themeOf(store);
    const keep = useKeepStore();
    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={testid}>
            <nav className="flex flex-wrap items-center gap-1 text-xs text-zinc-500" aria-label="Breadcrumb" data-testid={`${testid}-trail`}>
                <Link to="/directory" className="inline-flex items-center gap-1 hover:text-zinc-900" data-testid={`${testid}-home`}><Home size={12} /> Home</Link>
                {trail.map((c, i) => (
                    <span key={`${c.label}-${i}`} className="inline-flex items-center gap-1">
                        <ChevronRight size={12} className="text-zinc-300" />
                        {c.to && i < trail.length - 1
                            ? <Link to={keep(c.to)} className="hover:text-zinc-900" data-testid={`${testid}-${i}`}>{c.label}</Link>
                            : <span className="font-semibold text-zinc-900" data-testid={`${testid}-${i}`} aria-current="page">{c.label}</span>}
                    </span>
                ))}
            </nav>
            {pills.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testid}-pills`}>
                    {pills.map((p) => (
                        <Link key={p.key} to={keep(p.to)} data-testid={`${testid}-pill-${p.key}`} aria-pressed={!!p.active}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${p.active ? "border-transparent" : "border-zinc-200 text-zinc-700 hover:border-zinc-400"} ${p.count === 0 ? "opacity-40" : ""}`}
                            style={p.active ? { background: theme.primary, color: theme.on || "#fff" } : undefined}>
                            {p.label}
                            {p.count != null && <span className={`font-mono text-[10px] ${p.active ? "opacity-80" : "text-zinc-400"}`}>{p.count}</span>}
                        </Link>
                    ))}
                </div>
            )}
            {right && <div className="ml-auto">{right}</div>}
        </div>
    );
}

/** The Ready-in pills every catalog page shows: All · Same Day · 3–5 Days · 2–4 Weeks. */
export function readyPills(base, counts = {}, active, items) {
    return [
        { key: "all", label: "All", to: base, active: !active, count: counts.all },
        ...items.map((r) => ({ key: r.key, label: r.label, to: `${base}?ready=${r.key}`, active: active === r.key, count: counts[r.key] })),
    ];
}

/** The same breadcrumb line as a slim white bar under the header, for pages that open on a photo or dark hero. */
export function CrumbBar(props) {
    return (
        <div className="border-b border-zinc-200 bg-white" data-testid="crumb-bar">
            <div className="mx-auto max-w-7xl px-4 py-2.5 sm:px-6"><Crumbs {...props} /></div>
        </div>
    );
}
